import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import { NavigationPrompt } from '@innovyze/stylovyze';
import { Button, IconButton, Stack } from '@mui/material';
import { FullPageWrapper } from '@Utils/styles';
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch } from 'react-redux';
import {
  CostTableDetailsParams,
  CostTableDetailData,
} from '@Types/costTable.types';
import {
  useGlobalization,
  NotFoundPage,
  WarningDialog,
  error,
  success,
} from '@innovyze/stylovyze';
import { useCostTableDetails } from '@Hooks/useCostTableSelectors';
import {
  getCostTableDetailsAction,
  invalidateCostTablesOverview,
} from '@Actions/costTables.actions';
import RenameCostTableDialog from '@Components/RenameCostTableDialog';
import CostUploadDialog from '@Components/CostUploadDialog';
import CostTableConfiguration from '@Components/CostTableConfiguration';
import CostTableInfoBanner from '@Components/CostTableInfoBanner';
import CostTableDetailsGrid from '@Components/CostTableDetailGrid';
import SaveConfirmationDialog from '@Components/SaveConfirmationDialog';
import {
  refreshCostTableData,
  updateCostTableName,
  updateCostTableData as updateCostTableDataService,
  updateCostTableConfiguration,
} from '@Services/costTables.services';
import { useCostTableTracking } from '@Hooks/useCostTableTracking';
import { useHasEditAccess } from '@Hooks/useHasEditAccess';
import {
  getLengthUnitCostCsvColumnLabel,
  isLengthBasedCostPer,
  normalizeCsvLengthUnitCostUpdates,
} from '@Utils/lengthUnitCost';

const CostTableDetails: React.FC = () => {
  const { t } = useGlobalization();
  const dispatch = useDispatch();
  const { costTableId } = useParams<CostTableDetailsParams>();
  const hasEditAccess = useHasEditAccess();

  const {
    trackView,
    trackRenameSuccess,
    trackRenameFailed,
    trackSaveSuccess,
    trackSaveFailed,
  } = useCostTableTracking();

  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isCSVUploadOpen, setIsCSVUploadOpen] = useState(false);
  const [saveConfirmationOpen, setSaveConfirmationOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [updatedValues, setUpdatedValues] = useState<
    Record<string, CostTableDetailData>
  >({});

  const history = useHistory();
  const hasUnsavedChanges = Object.keys(updatedValues).length > 0;

  const { costTable, costTableData, isLoading, isWaiting, successful } =
    useCostTableDetails();
  const isLengthBased = isLengthBasedCostPer(costTable?.costPer);

  useEffect(() => {
    if (costTableId) {
      dispatch(getCostTableDetailsAction({ costTableId }));
    }
  }, [costTableId, dispatch]);

  useEffect(() => {
    if (costTableId) {
      trackView({ costtableid: costTableId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- page-view fires once on mount; adp initialises async causing trackView ref to change
  }, []);

  const [showRefreshWarning, setShowRefreshWarning] = useState(false);

  const executeRefresh = async () => {
    try {
      setUpdatedValues({});
      await refreshCostTableData(costTableId);
      dispatch(getCostTableDetailsAction({ costTableId }));
      dispatch(invalidateCostTablesOverview());
      dispatch(success(t('Cost table data refreshed successfully')));
    } catch (err) {
      console.error('Error refreshing cost table data:', err);
      dispatch(error(t('Unable to refresh cost table data')));
    }
  };

  const handleRefresh = () => {
    if (hasUnsavedChanges) {
      setShowRefreshWarning(true);
    } else {
      executeRefresh();
    }
  };

  const handleRename = async (newCostTableName: string) => {
    if (!costTableId) return;

    try {
      await updateCostTableName({
        costTableId: costTableId,
        newCostTableName: newCostTableName,
      });

      setIsRenameOpen(false);
      dispatch(success(t('Cost table renamed successfully')));
      dispatch(getCostTableDetailsAction({ costTableId }));
      dispatch(invalidateCostTablesOverview());
      trackRenameSuccess({ costtableid: costTableId });
    } catch (err: any) {
      console.error('Error renaming cost table:', err);
      dispatch(error(t('Unable to rename cost table')));
      trackRenameFailed({
        costtableid: costTableId,
        error: String(err?.message ?? err),
      });
    }
  };

  const handleUpdatedValuesChange = (
    newUpdatedValues: Record<string, CostTableDetailData>
  ) => {
    setUpdatedValues(newUpdatedValues);
  };

  const handleCsvUpdatedValuesChange = (
    newUpdatedValues: Record<string, CostTableDetailData>
  ) => {
    setUpdatedValues(
      isLengthBased
        ? normalizeCsvLengthUnitCostUpdates(newUpdatedValues)
        : newUpdatedValues
    );
  };

  const handleSave = () => {
    setSaveConfirmationOpen(true);
  };

  const handleSaveConfirmation = async () => {
    if (!costTableId || !costTable?.latestCostTableDataId) return;

    setIsSaving(true);
    const changecount = Object.keys(updatedValues).length;

    try {
      // Merge all existing data with updated values: backend expects full data set with updated values included
      const allData: Record<string, CostTableDetailData> = {};
      costTableData.forEach(row => {
        allData[row.id] = row;
      });
      // Override with updated values
      Object.keys(updatedValues).forEach(id => {
        allData[id] = updatedValues[id];
      });

      await updateCostTableDataService({
        costTableId,
        latestCostTableDataId: costTable.latestCostTableDataId,
        updatedValues: allData,
      });

      setSaveConfirmationOpen(false);
      setUpdatedValues({});
      dispatch(
        success(t('Cost Table saved. All unit costs have been updated.'))
      );
      dispatch(getCostTableDetailsAction({ costTableId }));
      dispatch(invalidateCostTablesOverview());
      trackSaveSuccess({ costtableid: costTableId, changecount });
    } catch (err: any) {
      console.error('Error saving cost table changes:', err);
      const errorMessage = err?.response?.data?.error_details?.error || '';
      console.log('Error message from response:', errorMessage);
      if (errorMessage === 'Latest cost table data ID does not match') {
        dispatch(
          error(
            t(
              'Unable to save: the cost table has been modified by another user. Please refresh and try again.'
            )
          )
        );
      } else {
        dispatch(error(t('Unable to save cost table changes')));
      }

      setSaveConfirmationOpen(false);
      trackSaveFailed({
        costtableid: costTableId,
        error: errorMessage || String(err?.message ?? err),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const cancelSaveConfirmation = () => {
    setSaveConfirmationOpen(false);
  };

  const handleUnitUpdate = async (newUnit: string) => {
    if (!costTableId) return;

    try {
      await updateCostTableConfiguration({
        costTableId,
        newUnit,
      });

      dispatch(success(t('Cost table unit updated successfully')));
      dispatch(getCostTableDetailsAction({ costTableId }));
      dispatch(invalidateCostTablesOverview());
    } catch (err) {
      console.error('Error updating cost table unit:', err);
      dispatch(error(t('Unable to update cost table unit')));
      throw err;
    }
  };

  if (!costTableId) {
    return (
      <FullPageWrapper title={t('Error')} applyPadding={true}>
        <div>{t('Cost Table ID is required')}</div>
      </FullPageWrapper>
    );
  }

  if (successful === false) {
    return <NotFoundPage />;
  }

  if (!costTable) {
    return <FullPageWrapper isLoading={true} />;
  }

  return (
    <>
      {costTable?.name && (
        <RenameCostTableDialog
          isOpen={isRenameOpen}
          setIsOpen={setIsRenameOpen}
          onRename={handleRename}
          modelName={costTable.name}
        />
      )}
      <SaveConfirmationDialog
        isOpen={saveConfirmationOpen}
        onClose={cancelSaveConfirmation}
        onConfirm={handleSaveConfirmation}
        isLoading={isSaving}
      />
      <WarningDialog
        open={showRefreshWarning}
        title={t('Lose Changes')}
        confirmText={t('OK')}
        cancelText={t('Cancel')}
        onConfirm={() => {
          setShowRefreshWarning(false);
          executeRefresh();
        }}
        onCancel={() => setShowRefreshWarning(false)}>
        {t('This will lose any changes made.')}
      </WarningDialog>

      <NavigationPrompt
        when={hasUnsavedChanges}
        navigate={history.push}
        shouldBlockNavigation={() => true}
        title={t('Unsaved Changes')}
        content={t(
          'You have unsaved changes to this cost table. If you leave now, your changes will be lost.'
        )}
        cancelText={t('Go back to Cost Table')}
        confirmText={t('Discard Changes')}
        onNavigateAway={() => setUpdatedValues({})}
        shouldBlockReload={hasUnsavedChanges}
      />

      <CostUploadDialog
        open={isCSVUploadOpen}
        onClose={() => setIsCSVUploadOpen(false)}
        allowAdditionalColumns={false}
        costTableId={costTableId}
        expectedColumns={[
          { header: 'id', dataType: 'string', required: true },
          {
            header: 'assetGroupSet',
            dataType: 'string',
            required: true,
            displayName: t('Asset Group Name'),
          },
          {
            header: 'description',
            dataType: 'string',
            required: true,
            allowEmpty: true,
            displayName: t('Description'),
          },
          {
            header: 'assetCount',
            dataType: 'number',
            required: true,
            allowEmpty: true,
            displayName: t('Asset Count'),
          },
          {
            header: 'unitCost',
            dataType: 'number',
            required: true,
            allowEmpty: true,
            displayName: isLengthBased
              ? getLengthUnitCostCsvColumnLabel(t)
              : t('Unit Cost'),
          },
        ]}
        keyField="id"
        comparableFields={['unitCost']}
        existingData={costTableData}
        onUpdatedValuesChange={handleCsvUpdatedValuesChange}
      />

      <FullPageWrapper
        title={costTable?.name || 'Cost Table Details'}
        breadcrumbs={[
          { title: t('Analysis'), link: '/analysis' },
          { title: t('Cost Tables'), link: '/cost-tables' },
        ]}
        minorHeader={
          hasEditAccess ? (
            <IconButton
              size="small"
              onClick={() => costTable?.name && setIsRenameOpen(true)}
              disabled={!costTable?.name || isLoading}>
              <EditIcon fontSize="small" />
            </IconButton>
          ) : undefined
        }
        secondary={
          hasEditAccess ? (
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setIsCSVUploadOpen(true)}
                disabled={isWaiting}>
                {t('Upload Costs')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={Object.keys(updatedValues).length === 0 || isWaiting}>
                {isWaiting ? t('Saving...') : t('Save Cost Table')}
              </Button>
            </Stack>
          ) : undefined
        }
        applyPadding={true}>
        <Stack spacing={3}>
          {costTable && (
            <CostTableConfiguration
              data={costTable}
              onUpdate={handleUnitUpdate}
              loading={isLoading}
            />
          )}
          {costTable && <CostTableInfoBanner />}
          <CostTableDetailsGrid
            data={costTableData}
            updatedValues={updatedValues}
            onUpdatedValuesChange={handleUpdatedValuesChange}
            onRefresh={handleRefresh}
            loading={isLoading}
            disableExport={hasUnsavedChanges}
            costTableId={costTableId}
            costTableName={costTable?.name}
            costPer={costTable?.costPer}
          />
        </Stack>
      </FullPageWrapper>
    </>
  );
};

export default CostTableDetails;
